// Silueta corporal del 003 (Hospitalización, bloque H — "se pintará la región o sistema afectado en el gráfico"). Fase 7
// (opcional): el papel trae 4 figuras (adulto de frente y de espalda, dos perfiles). Aquí son figuras esquemáticas hechas de
// regiones simples (cabeza, cuello, tórax, abdomen, pelvis, brazos, piernas) que se marcan con un clic; la geometría es UNA
// sola (este archivo) y la usan el widget de pantalla (`SiluetaCorporal.tsx`) y el dibujo vectorial del PDF firmado.
// Equivalencia funcional, no facsímil: cada vista se dibuja en un recuadro de 100 × 220 unidades.
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK } from './layout';

export type VistaSilueta = 'frente' | 'espalda' | 'perfilDerecho' | 'perfilIzquierdo';

export type FormaSilueta = { readonly tipo: 'rect'; readonly x: number; readonly y: number; readonly w: number; readonly h: number } | { readonly tipo: 'elipse'; readonly cx: number; readonly cy: number; readonly rx: number; readonly ry: number };

export interface RegionSilueta {
  readonly clave: string;
  readonly etiqueta: string;
  readonly forma: FormaSilueta;
}

export const ANCHO_VISTA_SILUETA = 100;
export const ALTO_VISTA_SILUETA = 220;

const rect = (x: number, y: number, w: number, h: number): FormaSilueta => ({ tipo: 'rect', x, y, w, h });
const cabeza: RegionSilueta = { clave: 'cabeza', etiqueta: 'Cabeza', forma: { tipo: 'elipse', cx: 50, cy: 16, rx: 13, ry: 15 } };
const cuello: RegionSilueta = { clave: 'cuello', etiqueta: 'Cuello', forma: rect(44, 30, 12, 10) };

/** Frente y espalda comparten el dibujo; cambian los rótulos y qué lado del paciente queda a la izquierda del papel. */
const cuerpoDe = (lado: 'frente' | 'espalda'): readonly RegionSilueta[] => {
  const izquierdaDelDibujo = lado === 'frente' ? 'derecho' : 'izquierdo';
  const derechaDelDibujo = lado === 'frente' ? 'izquierdo' : 'derecho';
  return [
    cabeza,
    cuello,
    { clave: 'torax', etiqueta: lado === 'frente' ? 'Tórax' : 'Espalda alta', forma: rect(30, 40, 40, 36) },
    { clave: 'abdomen', etiqueta: lado === 'frente' ? 'Abdomen' : 'Región lumbar', forma: rect(32, 76, 36, 28) },
    { clave: 'pelvis', etiqueta: lado === 'frente' ? 'Pelvis' : 'Glúteos', forma: rect(32, 104, 36, 18) },
    { clave: `brazo-${izquierdaDelDibujo}`, etiqueta: `Brazo ${izquierdaDelDibujo}`, forma: rect(14, 42, 13, 70) },
    { clave: `brazo-${derechaDelDibujo}`, etiqueta: `Brazo ${derechaDelDibujo}`, forma: rect(73, 42, 13, 70) },
    { clave: `pierna-${izquierdaDelDibujo}`, etiqueta: `Pierna ${izquierdaDelDibujo}`, forma: rect(33, 124, 15, 90) },
    { clave: `pierna-${derechaDelDibujo}`, etiqueta: `Pierna ${derechaDelDibujo}`, forma: rect(52, 124, 15, 90) },
  ];
};

const perfilDe = (): readonly RegionSilueta[] => [
  cabeza,
  cuello,
  { clave: 'torax', etiqueta: 'Tórax', forma: rect(36, 40, 28, 36) },
  { clave: 'abdomen', etiqueta: 'Abdomen', forma: rect(36, 76, 28, 28) },
  { clave: 'pelvis', etiqueta: 'Pelvis', forma: rect(36, 104, 28, 18) },
  { clave: 'brazo', etiqueta: 'Brazo', forma: rect(43, 42, 12, 70) },
  { clave: 'pierna', etiqueta: 'Pierna', forma: rect(38, 124, 24, 90) },
];

export const VISTAS_SILUETA: readonly { readonly vista: VistaSilueta; readonly etiqueta: string; readonly regiones: readonly RegionSilueta[] }[] = [
  { vista: 'frente', etiqueta: 'Frente', regiones: cuerpoDe('frente') },
  { vista: 'espalda', etiqueta: 'Espalda', regiones: cuerpoDe('espalda') },
  { vista: 'perfilDerecho', etiqueta: 'Perfil derecho', regiones: perfilDe() },
  { vista: 'perfilIzquierdo', etiqueta: 'Perfil izquierdo', regiones: perfilDe() },
];

/** Clave de una región marcada: `<vista>:<región>`. */
export const claveRegionSilueta = (vista: VistaSilueta, region: string): string => `${vista}:${region}`;

export const CLAVES_SILUETA_VALIDAS: ReadonlySet<string> = new Set(VISTAS_SILUETA.flatMap((v) => v.regiones.map((r) => claveRegionSilueta(v.vista, r.clave))));

/** Wire format serializable (modo 'msp', campo `siluetaCorporal`): las claves `<vista>:<región>` marcadas. */
export interface DatosSiluetaPdf {
  readonly regiones: readonly string[];
}

/** Rótulos legibles de lo marcado, agrupados por vista: "Frente: Tórax, Brazo derecho · Espalda: ...". */
export function resumenSilueta(regiones: readonly string[]): string {
  const marcadas = new Set(regiones);
  return VISTAS_SILUETA.flatMap((v) => {
    const nombres = v.regiones.filter((r) => marcadas.has(claveRegionSilueta(v.vista, r.clave))).map((r) => r.etiqueta);
    return nombres.length ? [`${v.etiqueta}: ${nombres.join(', ')}`] : [];
  }).join(' · ');
}

const ALTO_TITULO = 14;
const ALTO_ROTULO = 12;
const ALTO_LEYENDA = 14;

/** Espacio vertical a reservar con `cursor.dibujo(altoDibujoSilueta(datos), ...)` — 0 si no hay regiones marcadas. */
export function altoDibujoSilueta(datos: DatosSiluetaPdf): number {
  return datos.regiones.length === 0 ? 0 : ALTO_TITULO + ALTO_ROTULO + ALTO_VISTA_SILUETA + ALTO_LEYENDA;
}

const CLAY = rgb(0xb5 / 255, 0x56 / 255, 0x3c / 255);
const RELLENO_SUAVE = rgb(0xf3 / 255, 0xf5 / 255, 0xf6 / 255);

/** Dibuja las 4 vistas con las regiones marcadas rellenas, sobre una `PDFPage` cruda entregada por `CursorPdf.dibujo()`. */
export function dibujarSilueta(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosSiluetaPdf): void {
  if (datos.regiones.length === 0) return;
  const marcadas = new Set(datos.regiones);
  let y = origen.yTop - 10;
  pagina.drawText('Silueta corporal — regiones afectadas', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO;

  const escala = Math.min(1, anchoDisponible / (VISTAS_SILUETA.length * (ANCHO_VISTA_SILUETA + 10)));
  const anchoVista = ANCHO_VISTA_SILUETA * escala;
  const paso = anchoVista + 10 * escala;
  const yRotulo = y;
  y -= ALTO_ROTULO;
  const yTopFigura = y;

  for (const [i, v] of VISTAS_SILUETA.entries()) {
    const x0 = origen.x + i * paso;
    pagina.drawText(v.etiqueta, { x: x0, y: yRotulo, size: 7.5, font: fontRegular, color: INK });
    for (const r of v.regiones) {
      const activa = marcadas.has(claveRegionSilueta(v.vista, r.clave));
      const opciones = { borderColor: activa ? CLAY : BORDE_SUTIL, borderWidth: 0.8, color: activa ? CLAY : RELLENO_SUAVE, opacity: activa ? 0.75 : 1 };
      if (r.forma.tipo === 'rect') {
        pagina.drawRectangle({ x: x0 + r.forma.x * escala, y: yTopFigura - (r.forma.y + r.forma.h) * escala, width: r.forma.w * escala, height: r.forma.h * escala, ...opciones });
      } else {
        pagina.drawEllipse({ x: x0 + r.forma.cx * escala, y: yTopFigura - r.forma.cy * escala, xScale: r.forma.rx * escala, yScale: r.forma.ry * escala, ...opciones });
      }
    }
  }
  const yLeyenda = yTopFigura - ALTO_VISTA_SILUETA * escala - 10;
  pagina.drawText('Regiones marcadas en rojo. Perfiles: la región del lado indicado.', { x: origen.x, y: yLeyenda, size: 7, font: fontRegular, color: GRIS_OSCURO });
}
